<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div :class="bodyClassName">
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-plug"></i> {{ _('Partner integrations') }}</h3>
          <p class="help-block">{{ _('Integrations built by Johku\'s partners can use your shop\'s data only with your permission. You decide what each integration may do, and you can revoke its access at any time.') }}</p>
        </div>
      </div>

      <template v-if="partnerApiEnabled">
      <div class="partner-section" data-search="partner-pending" v-if="pending.length">
        <h4><i class="fa fa-fw fa-bell"></i> {{ _('Pending requests') }} <span class="badge">{{ pending.length }}</span></h4>
        <div class="panel panel-warning" v-for="connection in pending" :key="connection.id">
          <div class="panel-heading">
            <h4 class="panel-title">
              {{ integrationName(connection) }}
              <span :class="partnerBadgeClass(connection.partner)">{{ partnerBadge(connection.partner) }}</span>
            </h4>
          </div>
          <div class="panel-body">
            <p>
              {{ _('By') }} {{ partnerName(connection.partner) }}
              <span v-if="connection.partner && connection.partner.website"> · <a :href="safeUrl(connection.partner.website)" target="_blank" rel="noopener noreferrer">{{ connection.partner.website }}</a></span>
            </p>
            <p class="text-muted" v-if="connection.integration && connection.integration.description">{{ connection.integration.description }}</p>
            <p><strong>{{ _('The integration asks to:') }}</strong></p>
            <ul class="partner-scopes">
              <li v-for="scope in normalizeScopes(connection.scopes)" :key="scope">{{ scopeLabel(scope) }}</li>
            </ul>
            <p v-if="connection.requested_reason"><strong>{{ _('Reason') }}:</strong> {{ connection.requested_reason }}</p>
            <p class="text-muted small" v-if="connection.requested_at">{{ _('Requested') }}: {{ formatApiTime(connection.requested_at) }}</p>
            <div class="partner-buttons">
              <button type="button" class="btn btn-success" v-on:click="decide(connection, 'approve')" :disabled="busyId === connection.id"><i class="fa fa-fw fa-check"></i> {{ _('Approve') }}</button>
              <button type="button" class="btn btn-default" v-on:click="decide(connection, 'deny')" :disabled="busyId === connection.id"><i class="fa fa-fw fa-ban"></i> {{ _('Deny') }}</button>
            </div>
          </div>
        </div>
      </div>

      <div class="partner-section" data-search="partner-connect" v-if="connectable.length">
        <h4><i class="fa fa-fw fa-link"></i> {{ _('Connect your integrations') }}</h4>
        <p class="help-block">{{ _('You are a member of these partners. Choose what each integration may do in this shop and connect it.') }}</p>
        <div class="panel panel-default" v-for="item in connectable" :key="item.integration_id">
          <div class="panel-heading">
            <h4 class="panel-title">
              {{ item.integration_name }}
              <span :class="partnerBadgeClass({ status: item.partner_status })">{{ partnerBadge({ status: item.partner_status }) }}</span>
            </h4>
          </div>
          <div class="panel-body">
            <p>{{ _('By') }} {{ item.partner_name }}</p>
            <p class="text-muted" v-if="item.integration_description">{{ item.integration_description }}</p>
            <div class="checkbox" v-for="scope in normalizeScopes(item.scopes)" :key="scope">
              <label>
                <input type="checkbox" :value="scope" v-model="selectedScopes[item.integration_id]" />
                {{ scopeLabel(scope) }}
              </label>
            </div>
            <button type="button" class="btn btn-success" v-on:click="connect(item)" :disabled="busyId === 'connect-' + item.integration_id || !(selectedScopes[item.integration_id] || []).length"><i class="fa fa-fw fa-link"></i> {{ _('Connect') }}</button>
          </div>
        </div>
      </div>

      <div class="partner-section" data-search="partner-active">
        <h4><i class="fa fa-fw fa-circle-check"></i> {{ _('Active integrations') }}</h4>
        <p class="text-muted" v-if="loaded && !active.length">{{ _('No partner integrations are connected to your shop.') }}</p>
        <div class="partner-table-wrap" v-if="active.length">
          <table class="table table-striped">
            <thead>
              <tr>
                <th>{{ _('Integration') }}</th>
                <th>{{ _('Permissions') }}</th>
                <th>{{ _('Connected') }}</th>
                <th>{{ _('Last activity') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="connection in active" :key="connection.id">
                <td>
                  <strong>{{ integrationName(connection) }}</strong>
                  <div>
                    {{ partnerName(connection.partner) }}
                    <span :class="partnerBadgeClass(connection.partner)">{{ partnerBadge(connection.partner) }}</span>
                  </div>
                </td>
                <td>
                  <div v-for="scope in normalizeScopes(connection.scopes)" :key="scope">{{ scopeLabel(scope) }}</div>
                </td>
                <td>
                  {{ formatApiTime(connection.decided_at || connection.requested_at) }}
                  <div class="small text-muted" v-if="connection.decided_by_email">{{ connection.decided_by_email }}</div>
                  <div class="small text-muted">{{ methodLabel(connection.method) }}</div>
                </td>
                <td>{{ connection.last_used_at ? formatApiTime(connection.last_used_at) : _('Not used yet') }}</td>
                <td class="text-right">
                  <button type="button" class="btn btn-danger btn-sm" v-on:click="revoke(connection)" :disabled="busyId === connection.id"><i class="fa fa-fw fa-ban"></i> {{ _('Revoke') }}</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      </template>

      <div class="partner-section" data-search="partner-atlas" v-if="atlasSettingShown && atlas">
        <h4><i class="fa fa-fw fa-map"></i> {{ _('Johku Atlas for partners') }}</h4>
        <p class="help-block">{{ _('Johku Atlas lets partners approved by Johku search the products of all Johku shops, for example to show them on travel sites. Partners see what your storefront shows publicly, and customers book on your storefront. Booking through a partner\'s own site still needs your permission.') }}</p>
        <div class="checkbox">
          <label>
            <input type="checkbox" :checked="atlas.listed" :disabled="atlasBusy" v-on:change="setAtlasListed($event.target.checked)" />
            {{ _('Show my products to partners in Johku Atlas') }}
          </label>
        </div>
        <p class="text-muted small" v-if="!atlas.listed && atlas.opted_out_by">{{ _('Hidden by %s').replace('%s', atlas.opted_out_by) }}, {{ formatApiTime(atlas.opted_out_at) }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import partnerapi from '~/lib/partnerapi'
import settingshelpers from '~/lib/settingshelpers'

// Hidden while the partner API is piloted: how shops opt out of Atlas is still to be decided. Showing it again also
// needs the page shown to every shop (SettingsMenu, componenttypes, settingssearch), since Atlas lists them all.
const ATLAS_SETTING_SHOWN = false

export default {
  mixins: [components, partnerapi, settingshelpers],
  data () {
    return {
      connections: [],
      available: [],
      selectedScopes: {},
      loaded: false,
      busyId: null,
      atlas: null,
      atlasBusy: false,
      atlasSettingShown: ATLAS_SETTING_SHOWN
    }
  },
  computed: {
    partnerApiEnabled () {
      return this.isFeatureEnabled('partnerapi')
    },
    pending () {
      return this.connections.filter(c => c.status === 'requested')
    },
    active () {
      return this.connections.filter(c => c.status === 'active')
    },
    // Integrations already connected to this shop are not offered again
    connectable () {
      const connected = new Set(this.active.map(c => String(c.integration && c.integration.id)))
      return this.available.filter(item => !connected.has(String(item.integration_id)))
    }
  },
  methods: {
    url (path) {
      return this.baseDomain + this.shopId + '/partnerconnections' + (path || '') + '.json?apikey=' + this.apiKey
    },
    integrationName (connection) {
      return connection.integration && connection.integration.name ? connection.integration.name : ''
    },
    partnerName (partner) {
      return partner && partner.name ? partner.name : ''
    },
    partnerBadge (partner) {
      return partner && partner.status === 'verified' ? this._('Verified by Johku') : this._('Pilot')
    },
    partnerBadgeClass (partner) {
      return 'label partner-badge ' + (partner && partner.status === 'verified' ? 'label-success' : 'label-info')
    },
    // Only http(s) links from the partner's website field
    safeUrl (url) {
      const text = String(url || '').trim()
      if (/^https?:\/\//i.test(text)) {
        return text
      }
      return text ? 'https://' + text.replace(/^[a-z]+:/i, '').replace(/^\/+/, '') : undefined
    },
    async loadConnections () {
      try {
        const { data } = await axios.get(this.url())
        this.connections = this.listData(data)
      } catch (e) {
        this.showError(e, this._('Loading the partner integrations failed.'))
      } finally {
        this.loaded = true
      }
    },
    // Only partner members get anything here; for everyone else the section stays hidden, also on errors
    async loadAvailable () {
      try {
        const { data } = await axios.get(this.url('/available'))
        const items = this.listData(data)
        const selected = {}
        for (const item of items) {
          selected[item.integration_id] = this.selectedScopes[item.integration_id] || this.normalizeScopes(item.scopes)
        }
        this.selectedScopes = selected
        this.available = items
      } catch (e) {
        this.available = []
      }
    },
    reload () {
      return this.partnerApiEnabled ? Promise.all([this.loadConnections(), this.loadAvailable()]) : Promise.resolve()
    },
    async loadAtlas () {
      try {
        const { data } = await axios.get(this.url('/atlas'))
        this.atlas = data
      } catch (e) {
        this.showError(e, this._('Loading the Johku Atlas setting failed.'))
      }
    },
    async setAtlasListed (listed) {
      this.atlasBusy = true
      try {
        const { data } = await axios.patch(this.url('/atlas'), { listed })
        this.atlas = data
        this.showNotification(listed ? this._('Partners can find your products in Johku Atlas.') : this._('Your products are hidden from partners in Johku Atlas.'))
      } catch (e) {
        this.showError(e, null, true)
      } finally {
        this.atlasBusy = false
      }
    },
    async patchConnection (connection, action, successMessage) {
      this.busyId = connection.id
      try {
        await axios.patch(this.url('/' + connection.id), { action })
        await this.reload()
        this.showNotification(successMessage)
      } catch (e) {
        this.showError(e, null, true)
      } finally {
        this.busyId = null
      }
    },
    decide (connection, action) {
      if (action === 'approve') {
        this.patchConnection(connection, 'approve', this._('The integration is now connected to your shop.'))
      } else {
        this.patchConnection(connection, 'deny', this._('The request was denied.'))
      }
    },
    revoke (connection) {
      this.showDialog({
        title: this._('Revoke the access of %s?').replace('%s', this.integrationName(connection)),
        text: this._('The integration can no longer use your shop\'s data. To use it again, it has to ask for your permission again.'),
        buttons: [
          { title: this._('Cancel'), icon: 'ban', handler: () => {} },
          {
            title: this._('Revoke'),
            class: 'danger',
            icon: 'ban',
            handler: () => {
              this.patchConnection(connection, 'revoke', this._('The integration\'s access was revoked.'))
            }
          }
        ]
      })
    },
    async connect (item) {
      const scopes = this.selectedScopes[item.integration_id] || []
      if (!scopes.length) {
        return
      }
      this.busyId = 'connect-' + item.integration_id
      try {
        await axios.post(this.url(), { integration_id: item.integration_id, scopes })
        await this.reload()
        this.showNotification(this._('The integration is now connected to your shop.'))
      } catch (e) {
        this.showError(e, null, true)
      } finally {
        this.busyId = null
      }
    }
  },
  mounted () {
    this.reload()
    if (this.atlasSettingShown) {
      this.loadAtlas()
    }
  }
}
</script>

<style scoped>
.partner-section {
  margin-bottom: 25px;
}
.partner-table-wrap {
  overflow-x: auto;
}
.partner-scopes {
  padding-left: 20px;
}
.partner-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.partner-badge {
  margin-left: 6px;
  vertical-align: middle;
}
</style>
